import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Clock, Users, Truck, IndianRupee, ArrowRight, RefreshCw, ShoppingCart, Package, UserPlus } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { inr, fmtDate, StatusBadge } from "@/lib/utils";

export default function AdminDashboard() {
  const { t, tAny } = useLang();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();
  const loadDashboard = useCallback(() => { api.get("/admin/summary").then((r) => setSummary(r.data)).catch((e) => console.error(e)); api.get("/admin/orders").then((r) => setOrders(r.data.slice(0, 8))).catch((e) => console.error(e)); }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const cards = summary
    ? [
        { label: t("newOrders"), value: summary.new_orders, icon: Inbox, testid: "admin-card-new-orders" },
        { label: t("pendingApproval"), value: summary.pending_approval, icon: Clock, testid: "admin-card-pending" },
        { label: t("customers"), value: summary.customers, icon: Users, testid: "admin-card-customers" },
        { label: t("dispatch"), value: summary.dispatch, icon: Truck, testid: "admin-card-dispatch" },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{t("adminDashboardTitle")}</h1>
       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
         <p className="text-sm text-slate-500">{t("adminDashboardSub")}</p>
         <div className="flex flex-wrap items-center gap-2">
           <button type="button" className="sf-btn sf-btn-secondary inline-flex items-center gap-2" onClick={loadDashboard}>
             <RefreshCw className="h-4 w-4" /> Refresh
           </button>
           <button type="button" className="sf-btn sf-btn-primary inline-flex items-center gap-2" onClick={() => navigate("/admin/orders")}>
             <ShoppingCart className="h-4 w-4" /> Orders
           </button>
           <button type="button" className="sf-btn sf-btn-secondary inline-flex items-center gap-2" onClick={() => navigate("/admin/customers")}>
             <UserPlus className="h-4 w-4" /> Customers
           </button>
           <button type="button" className="sf-btn sf-btn-secondary inline-flex items-center gap-2" onClick={() => navigate("/admin/products")}>
             <Package className="h-4 w-4" /> Products
           </button>
         </div>
       </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="sf-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={c.testid}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.label}</span>
                <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">{c.value}</div>
              </div>
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <c.icon className="h-5 w-5 text-emerald-700" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="sf-card px-4 py-3 flex items-center justify-between" data-testid="admin-card-confirmed">
            <span className="text-slate-500">{t("confirmedLabel")}</span>
            <span className="font-bold text-slate-900">{summary.confirmed}</span>
          </div>
          <div className="sf-card px-4 py-3 flex items-center justify-between" data-testid="admin-card-delivered">
            <span className="text-slate-500">{t("deliveredLabel")}</span>
            <span className="font-bold text-slate-900">{summary.delivered}</span>
          </div>
          <div className="sf-card px-4 py-3 flex items-center justify-between" data-testid="admin-card-cancelled">
            <span className="text-slate-500">{t("cancelledLabel")}</span>
            <span className="font-bold text-slate-900">{summary.cancelled}</span>
          </div>
          <div className="sf-card px-4 py-3 flex items-center justify-between" data-testid="admin-card-total-due">
            <span className="text-slate-500 flex items-center gap-1">
              <IndianRupee className="h-3.5 w-3.5" /> {t("totalDue")}
            </span>
            <span className="font-bold text-rose-700">{inr(summary.total_due)}</span>
          </div>
        </div>
      )}

      <div className="sf-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display font-bold text-slate-900">{t("latestOrders")}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Most recent customer and assisted orders</p>
          </div>
          <button className="sf-btn sf-btn-secondary inline-flex items-center justify-center gap-2 text-sm" onClick={() => navigate("/admin/orders")} data-testid="view-all-orders-btn">
            {t("viewAll")} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr>
              <th className="sf-th">{t("orderNo")}</th>
              <th className="sf-th">{t("customerLbl")}</th>
              <th className="sf-th">{t("products")}</th>
              <th className="sf-th">{t("total")}</th>
              <th className="sf-th">{t("date")}</th>
              <th className="sf-th">{t("status")}</th>
              <th className="sf-th"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="sf-tr cursor-pointer" data-testid={`latest-order-${o.order_number}`} onClick={() => navigate(`/admin/orders?open=${o.id}`)}>
                <td className="sf-td font-mono-sf font-semibold text-slate-900">{o.order_number}</td>
                <td className="sf-td">{o.customer?.name}</td>
                <td className="sf-td">{o.items.length}</td>
                <td className="sf-td">{o.payment.total > 0 ? inr(o.payment.total) : "—"}</td>
                <td className="sf-td">{fmtDate(o.created_at)}</td>
                <td className="sf-td">
                  <StatusBadge status={o.status} />
                </td>
                <td className="sf-td text-right">
                  <ArrowRight className="h-4 w-4 text-slate-400 inline" />
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td className="sf-td text-slate-400" colSpan={7}>
                  {t("noneYet")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
